import {
    InitiateAuthCommand,
    RespondToAuthChallengeCommand,
    InitiateAuthCommandInput,
    InitiateAuthCommandOutput,
    RespondToAuthChallengeCommandInput,
    RespondToAuthChallengeCommandOutput,
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoClient, AWS_CONFIG } from "../config/aws-config";
import type { CognitoError, InitiateAuthResult, VerifyAuthResult } from "../types/cognito.types";
import { AuthTokens, User, CustomerData } from '../types/auth';
import Any = jasmine.Any;

class AuthService {
    private baseURL = AWS_CONFIG.apiBaseUrl;
    private currentUser: InitiateAuthCommandOutput | null = null;
    private userEmail: string = '';

    /**
     * Send login code to user's email
     */
    async sendLoginCode(email: string): Promise<void> {
            const params: InitiateAuthCommandInput = {
                AuthFlow: 'CUSTOM_AUTH',
                ClientId: AWS_CONFIG.clientId,
                AuthParameters: {
                    USERNAME: email,
                },
            };

            await new Promise(resolve => setTimeout(resolve, 1000));

            if (!email.includes('@')) {
                throw new Error('Invalid email address');
            }

            let result: InitiateAuthCommandOutput;
            try {
                const command = new InitiateAuthCommand(params);
                result = await cognitoClient.send(command);
            } catch (error) {
                throw this.handleError(error);
            }

            if (result.ChallengeName === 'CUSTOM_CHALLENGE') {
                this.currentUser = result;
                this.userEmail = email;
                sessionStorage.setItem('profileEmail', email);
            } else {
                throw new Error('Unexpected authentication flow');
            }
    }

    async verifyCode(email: string, code: string): Promise<{ tokens: AuthTokens; customerData: CustomerData }> {
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (!this.currentUser) {
            throw new Error('No active authentication session');
        }

        const params: RespondToAuthChallengeCommandInput = {
            ClientId: AWS_CONFIG.clientId,
            ChallengeName: this.currentUser.ChallengeName,
            Session: this.currentUser.Session,
            ChallengeResponses: {
                USERNAME: this.userEmail,
                ANSWER: code
            }
        };

        let result: RespondToAuthChallengeCommandOutput;
        try {
            const command = new RespondToAuthChallengeCommand(params);
            result = await cognitoClient.send(command);
        } catch (error) {
            throw this.handleError(error);
        }

        if (result.AuthenticationResult) {
            // Store authentication tokens
            const authResult = result.AuthenticationResult;

            if (!authResult?.AccessToken || !authResult?.IdToken || !authResult?.RefreshToken) {
                throw new Error('Authentication tokens not received');
            }

            const tokens: AuthTokens = {
                accessToken: authResult.AccessToken,
                idToken: authResult.IdToken,
                refreshToken: authResult.RefreshToken,
            };

            this.storeTokens(tokens);
            this.storeUser({ email });

            return {
                tokens,
                customerData: {
                    customerList: [],
                    customerCode: ''
                }
            };
        } else {
            throw new Error('Invalid code. Please try again');
        }
    }

    async validateToken(token: string): Promise<{ email: string; tokens: AuthTokens; customerData: CustomerData }> {
        const response = await fetch(`${this.baseURL}/webV2/visitorportal/register/validate`, {
            method: 'POST',
            headers: {
                Authorization: token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({})
        });

        const result = await response.json();
        const data = result.returnData;

        if (data.AuthenticationResult) {
            const tokens: AuthTokens = {
                accessToken: data.AuthenticationResult.AccessToken,
                idToken: data.AuthenticationResult.IdToken,
                refreshToken: data.AuthenticationResult.RefreshToken,
            };

            const customerData: CustomerData = {
                customerList: data.customerList,
                customerCode: data.customerCode
            };

            this.storeTokens(tokens);
            this.storeUser({ email: data.email });
            this.storeCustomerData(customerData);

            return {
                email: data.email,
                tokens,
                customerData,
            };
        } else {
            throw new Error('Invalid or expired token');
        }
    }

    /**
     * Handle and format Cognito errors
     */
    private handleError(error: unknown): Error {
        const cognitoError = error as CognitoError;

        if (cognitoError.name === 'UserNotFoundException' || cognitoError.name === 'UserLambdaValidationException') {
            return new Error('No account found with this email');
        }

        if (cognitoError.name === 'NotAuthorizedException') {
            return new Error('Invalid code. Please try again');
        }

        if (cognitoError.name === 'CodeMismatchException') {
            return new Error('Invalid verification code');
        }

        if (cognitoError.name === 'ExpiredCodeException') {
            return new Error('Code has expired. Please request a new one');
        }

        if (cognitoError.name === 'TooManyRequestsException') {
            return new Error('Too many attempts. Please try again later');
        }

        if (cognitoError.name === 'LimitExceededException') {
            return new Error('Rate limit exceeded. Please try again later');
        }

        return new Error(cognitoError.message || 'An error occurred. Please try again');
    }

    storeTokens(tokens: AuthTokens): void {
        sessionStorage.setItem('accessToken', tokens.accessToken);
        sessionStorage.setItem('idToken', tokens.idToken);
        sessionStorage.setItem('refreshToken', tokens.refreshToken);
    }

    storeUser(user: User): void {
        sessionStorage.setItem('profileEmail', user.email);
    }

    storeCustomerData(customerData: CustomerData): void {
        // console.log('Storing customer data:', customerData);
        if (customerData.customerCode) {
            sessionStorage.setItem('customerCode', customerData.customerCode);
        }
        if (customerData.customerList) {
            sessionStorage.setItem('customerList', JSON.stringify(customerData.customerList));
            const customerRecord = customerData.customerList.find((customer: any) => customer.customer_code === customerData.customerCode);
            sessionStorage.setItem('personType', customerRecord?.person_type);
            sessionStorage.setItem('peopleId', customerRecord?.people_id);
            sessionStorage.setItem('companyId', customerRecord?.company_id)
        }
    }

    getStoredTokens(): AuthTokens | null {
        const accessToken = sessionStorage.getItem('accessToken');
        const idToken = sessionStorage.getItem('idToken');
        const refreshToken = sessionStorage.getItem('refreshToken');

        if (accessToken && idToken && refreshToken) {
            return { accessToken, idToken, refreshToken };
        }
        return null;
    }

    getStoredUser(): User | null {
        const email = sessionStorage.getItem('profileEmail');
        return email ? { email } : null;
    }

    clearStorage(): void {
        sessionStorage.clear();
    }
}

export const authService = new AuthService();